import { useCallback, useEffect, useRef, useState } from "react";

export interface ISpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 12;

/** Find the nearest scrollable ancestor of an element. */
function getScrollParent(el: HTMLElement): HTMLElement {
  let parent = el.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    if (/(auto|scroll)/.test(style.overflowY)) return parent;
    parent = parent.parentElement;
  }
  return document.documentElement;
}

/** Get the viewport-relative bounds of the scroll container.
 *  For documentElement the container fills the full document, so use the
 *  visual viewport (window.innerHeight) instead — otherwise every element
 *  appears "in view" and no scroll ever fires. */
function getContainerViewportBounds(container: HTMLElement): {
  top: number;
  bottom: number;
  height: number;
} {
  if (container === document.documentElement) {
    return { top: 0, bottom: window.innerHeight, height: window.innerHeight };
  }
  const r = container.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, height: r.height };
}

/** Scroll target into the center of its scroll container.
 *  Returns a promise that resolves once scrolling has stopped. */
function scrollTargetIntoView(el: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const container = getScrollParent(el);
    const elRect = el.getBoundingClientRect();
    const containerBounds = getContainerViewportBounds(container);

    const elCenter = elRect.top + elRect.height / 2;
    const containerCenter = containerBounds.top + containerBounds.height / 2;
    const offset = elCenter - containerCenter;

    // If no scroll needed, resolve immediately
    if (Math.abs(offset) < 2) {
      resolve();
      return;
    }

    // Detect when scrolling stops by waiting for no scroll events for 100ms
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        container.removeEventListener("scroll", onScroll);
        resolve();
      }, 100);
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    container.scrollBy({ top: offset, behavior: "smooth" });

    // Safety fallback — resolve after 2s no matter what
    setTimeout(() => {
      container.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
      resolve();
    }, 2000);
  });
}

function calcRect(el: HTMLElement): ISpotlightRect | null {
  const domRect = el.getBoundingClientRect();

  // Treat hidden elements (zero dimensions) as not found — e.g. sidebar
  // nav items that are in the DOM but hidden on tablet/mobile via CSS.
  if (domRect.width === 0 && domRect.height === 0) return null;

  return {
    top: domRect.top - PADDING,
    left: domRect.left - PADDING,
    width: domRect.width + PADDING * 2,
    height: domRect.height + PADDING * 2,
  };
}

export const useSpotlight = (selector: string | null) => {
  const [rect, setRect] = useState<ISpotlightRect | null>(null);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const isScrollingRef = useRef(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const recalculate = useCallback(() => {
    if (!selector) {
      setRect(null);
      setTargetEl(null);
      return;
    }

    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      setRect(null);
      setTargetEl(null);
      return;
    }

    setRect(calcRect(el));
    setTargetEl(el);
  }, [selector]);

  // Scroll into view when selector changes, then reveal the spotlight after
  // the scroll finishes. Rect stays null during the scroll so the overlay
  // doesn't try to highlight a moving target.
  useEffect(() => {
    if (!selector) {
      setRect(null);
      setTargetEl(null);
      return;
    }

    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      setRect(null);
      setTargetEl(null);
      return;
    }

    // Check if element is already roughly in view
    const container = getScrollParent(el);
    const elRect = el.getBoundingClientRect();
    const containerBounds = getContainerViewportBounds(container);
    const inView =
      elRect.top >= containerBounds.top - 50 &&
      elRect.bottom <= containerBounds.bottom + 50;

    if (inView) {
      // Already visible — show spotlight immediately
      recalculate();
    } else {
      // Needs scrolling — hide spotlight, scroll, then reveal after scroll stops
      setRect(null);
      isScrollingRef.current = true;
      setIsScrolling(true);
      let cancelled = false;

      scrollTargetIntoView(el).then(() => {
        if (cancelled) return;
        isScrollingRef.current = false;
        setIsScrolling(false);
        recalculate();
      });

      return () => {
        cancelled = true;
        isScrollingRef.current = false;
        setIsScrolling(false);
      };
    }
  }, [selector, recalculate]);

  // Recalculate on resize/scroll — skip during programmatic smooth scroll
  const handleScrollOrResize = useCallback(() => {
    if (isScrollingRef.current) return;
    recalculate();
  }, [recalculate]);

  // Keep spotlight in sync with resize/user scroll
  useEffect(() => {
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);

    let resizeObserver: ResizeObserver | null = null;
    if (selector) {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        resizeObserver = new ResizeObserver(handleScrollOrResize);
        resizeObserver.observe(el);
      }
    }

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      resizeObserver?.disconnect();
    };
  }, [selector, handleScrollOrResize]);

  // Retry: if element not found yet, watch for it to appear in the DOM
  useEffect(() => {
    if (!selector) return;
    if (document.querySelector<HTMLElement>(selector)) return;

    const mutationObserver = new MutationObserver(() => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        mutationObserver.disconnect();
        isScrollingRef.current = true;
        scrollTargetIntoView(el).then(() => {
          isScrollingRef.current = false;
          recalculate();
        });
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => mutationObserver.disconnect();
  }, [selector, recalculate]);

  return { rect, targetEl, recalculate, isScrolling };
};
